import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Icon,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react'
import { FC } from 'react'
import { MdHistory } from 'react-icons/md'

import { IAprovacaoItem } from '@base/@types/providers/FollowUpFornecedor/AprovacaoProvider'

import {
  estadoRespostaBadge,
  statusAprovacaoBadge
} from '../../../utils/followUpFornecedor/badges'

// Formata "yyyy-MM-ddTHH:mm" sem conversão de fuso, igual ao CSV exportado
const formatDataHora = (value: string | null | undefined) => {
  const m = value?.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : null
}

interface Props {
  filtered: IAprovacaoItem[]
  selected: IAprovacaoItem[]
  pendentes: IAprovacaoItem[]
  allPendentesSelected: boolean
  onToggleSelect: (item: IAprovacaoItem) => void
  onSelectAll: () => void
  onClearSelection: () => void
  onSingleApprove: (item: IAprovacaoItem) => void
  onSingleReject: (item: IAprovacaoItem) => void
  onOpenHistorico: (item: {
    documentId: number
    productId: number
    nomeProduto: string
  }) => void
}

const AprovacaoTable: FC<Props> = ({
  filtered,
  selected,
  pendentes,
  allPendentesSelected,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  onSingleApprove,
  onSingleReject,
  onOpenHistorico
}) => (
  <>
    <Flex align="center" mb={3} gap={3}>
      <Text fontSize="13px" color="gray.500">
        {filtered.length} registro{filtered.length !== 1 ? 's' : ''}
        {pendentes.length > 0 && ` · ${pendentes.length} aguardando`}
      </Text>
      {pendentes.length > 0 && (
        <>
          <Button
            size="xs"
            variant="outline"
            colorScheme="green"
            onClick={onSelectAll}
            isDisabled={allPendentesSelected}
          >
            Selecionar todos pendentes
          </Button>
          <Button
            size="xs"
            variant="ghost"
            colorScheme="gray"
            onClick={onClearSelection}
            isDisabled={selected.length === 0}
          >
            Deselecionar todos
          </Button>
        </>
      )}
    </Flex>

    <Box
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      overflowX="auto"
      sx={{ th: { whiteSpace: 'nowrap' } }}
    >
      <Table size="sm">
        <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
          <Tr>
            <Th w="40px">
              <Checkbox
                sx={{
                  '& .chakra-checkbox__control': {
                    borderColor: 'gray.400',
                    borderWidth: '2px'
                  }
                }}
                isChecked={allPendentesSelected}
                isIndeterminate={selected.length > 0 && !allPendentesSelected}
                isDisabled={pendentes.length === 0}
                onChange={e =>
                  e.target.checked ? onSelectAll() : onClearSelection()
                }
              />
            </Th>
            <Th>Fornecedor</Th>
            <Th>Centro</Th>
            <Th>Cód. Produto</Th>
            <Th>Produto</Th>
            <Th>Doc.</Th>
            <Th isNumeric>A Fornecer</Th>
            <Th isNumeric>Nova Qtd.</Th>
            <Th>Data Orig.</Th>
            <Th>Nova Data</Th>
            <Th>Observação</Th>
            <Th>Qtd. Confirmada</Th>
            <Th>Data Confirmada</Th>
            <Th>Data de Resposta do Fornecedor</Th>
            <Th>Estado</Th>
            <Th>Status</Th>
            <Th>Histórico</Th>
            <Th>Ações</Th>
          </Tr>
        </Thead>
        <Tbody>
          {filtered.map(item => {
            const isSelected = selected.some(s => s.id === item.id)
            return (
              <Tr
                key={item.id}
                bg={isSelected ? 'green.50' : undefined}
                _hover={{ bg: isSelected ? 'green.50' : 'gray.50' }}
              >
                <Td>
                  <Checkbox
                    sx={{
                      '& .chakra-checkbox__control': {
                        borderColor: 'gray.400',
                        borderWidth: '2px'
                      }
                    }}
                    isChecked={isSelected}
                    isDisabled={item.statusAprovacao !== 0}
                    onChange={() => onToggleSelect(item)}
                  />
                </Td>
                <Td>
                  <Text fontWeight="500" fontSize="13px">
                    {item.fornecedor}
                  </Text>
                </Td>
                <Td fontSize="13px">{item.centro}</Td>
                <Td fontSize="13px" color="gray.600">
                  {item.productId}
                </Td>
                <Td>
                  <Text fontWeight="500" fontSize="13px">
                    {item.nomeProduto}
                  </Text>
                </Td>
                <Td fontSize="12px" color="gray.500">
                  {item.documentId}
                </Td>
                <Td isNumeric fontSize="13px">
                  {item.aFornecer}
                </Td>
                <Td fontSize="13px">
                  {item.novaQuantidade != null ? (
                    <Box display="inline-block">
                      {item.quantidadeConfirmadaAtual != null && (
                        <Box
                          as="s"
                          display="inline"
                          color="gray.400"
                          fontSize="11px"
                          mr={1}
                        >
                          {item.quantidadeConfirmadaAtual}
                        </Box>
                      )}
                      <Box
                        as="span"
                        display="inline"
                        color="green.600"
                        fontWeight="600"
                      >
                        {item.novaQuantidade}
                      </Box>
                    </Box>
                  ) : (
                    <Text color="gray.400">—</Text>
                  )}
                </Td>
                <Td fontSize="13px">
                  {new Date(item.dataEstimada).toLocaleDateString('pt-BR')}
                </Td>
                <Td fontSize="13px">
                  {(() => {
                    if (!item.novaDataEntrega) {
                      return <Text color="gray.400">—</Text>
                    }
                    const d = new Date(item.novaDataEntrega)
                    if (isNaN(d.getTime())) {
                      return <Text color="gray.400">—</Text>
                    }
                    const prev = item.dataConfirmadaAtual
                      ? new Date(item.dataConfirmadaAtual)
                      : null
                    return (
                      <Box display="inline-block">
                        {prev && !isNaN(prev.getTime()) && (
                          <Box
                            as="s"
                            display="inline"
                            color="gray.400"
                            fontSize="11px"
                            mr={1}
                          >
                            {prev.toLocaleDateString('pt-BR')}
                          </Box>
                        )}
                        <Box
                          as="span"
                          display="inline"
                          color="green.600"
                          fontWeight="600"
                        >
                          {d.toLocaleDateString('pt-BR')}
                        </Box>
                      </Box>
                    )
                  })()}
                </Td>
                <Td
                  fontSize="12px"
                  color="gray.600"
                  fontStyle="italic"
                  maxW="160px"
                >
                  {item.observacao ?? <Text color="gray.400">—</Text>}
                </Td>
                <Td fontSize="13px">
                  {item.quantidadeConfirmadaAtual != null ? (
                    <Text fontWeight="600" color="blue.600">
                      {item.quantidadeConfirmadaAtual}
                    </Text>
                  ) : (
                    <Text color="gray.400">—</Text>
                  )}
                </Td>
                <Td fontSize="13px">
                  {item.dataConfirmadaAtual ? (
                    <Text fontWeight="600" color="blue.600">
                      {new Date(item.dataConfirmadaAtual).toLocaleDateString(
                        'pt-BR'
                      )}
                    </Text>
                  ) : (
                    <Text color="gray.400">—</Text>
                  )}
                </Td>
                <Td fontSize="13px" whiteSpace="nowrap">
                  {formatDataHora(item.dataResposta) ?? (
                    <Text color="gray.400">—</Text>
                  )}
                </Td>
                <Td>{estadoRespostaBadge(item.estadoResposta)}</Td>
                <Td>{statusAprovacaoBadge(item.statusAprovacao)}</Td>
                <Td>
                  <Tooltip
                    label="Ver histórico do par"
                    hasArrow
                    placement="top"
                  >
                    <IconButton
                      aria-label="Histórico"
                      icon={<Icon as={MdHistory} boxSize={4} />}
                      size="xs"
                      variant="outline"
                      colorScheme="purple"
                      onClick={() =>
                        onOpenHistorico({
                          documentId: item.documentId,
                          productId: item.productId,
                          nomeProduto: item.nomeProduto
                        })
                      }
                    />
                  </Tooltip>
                </Td>
                <Td>
                  {item.statusAprovacao === 0 && (
                    <HStack spacing={1}>
                      <Button
                        size="xs"
                        colorScheme="green"
                        variant="outline"
                        onClick={() => onSingleApprove(item)}
                      >
                        Aprovar
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => onSingleReject(item)}
                      >
                        Recusar
                      </Button>
                    </HStack>
                  )}
                </Td>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </Box>
  </>
)

export default AprovacaoTable
